/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ErrorMessage,
  LoginAuthUserInputFields
} from '@com-delta-idp/mnx-login-ui';
import { OauthAEMConfig } from '@com-delta-idp/mnx-oauth';
import { AnalyticsService } from '@com-delta-idp/mnx-utils';
import { BehaviorSubject } from 'rxjs';
import {
  AEM_EMAIL,
  DEFAULT_MODAL_ERROR_CODE,
  RECOGNITION_PATHNAME_REGEX,
  REFRESH_URL,
  VIEW_NAME
} from '../login-modal/constants/login-constant';
import { ANALYTICS_EVENTS_LOGIN } from '../login-modal/constants/analytics-constants';
import { LoginModalContent } from '../login-modal/models/aem';
import { loginAndGetDashboardResponse } from '../login-modal/models/login-authentication-screen.interface';
import { WifiPortalService } from '../login-modal/services/wifi-portal/wifi-portal.service';

import { regexList } from '../validator';
import { LoginHelperUtilsSupportService } from './login-helper-utils-support.service';
import { NuTravelSkybonusUtilService } from './nuTravelSkyBonusUtils/nu-travel-skybonus-util.service';

@Injectable({
  providedIn: 'root'
})
export class LoginHelperUtils extends LoginHelperUtilsSupportService {
  recognitionRoute = new BehaviorSubject<boolean>(false);
  fromAdmin$ = new BehaviorSubject<boolean>(false);
  wifiPortalService = inject(WifiPortalService);
  route = inject(ActivatedRoute);
  nuTravelSkyBonusUtilService = inject(NuTravelSkybonusUtilService);
  analytics = inject(AnalyticsService);
  tokenResponse = '';

  isUserName(value: string): boolean {
    if (
      value.match(regexList.userIdLength) &&
      value.match(regexList.userName) &&
      value.match(regexList.isNaN)
    ) {
      return true;
    }
    return false;
  }

  isSkymilesNumber(value: string): boolean {
    if (
      value.match(regexList.userIdLength) &&
      value.match(regexList.skyMilesNumber)
    ) {
      return true;
    }
    return false;
  }

  isEmailValid(value: string): boolean {
    return !!value.match(regexList.email);
  }

  maskEmail(
    emailMessage: string,
    maskedEmail: string | undefined | null
  ): string {
    return maskedEmail
      ? emailMessage.replace(AEM_EMAIL, maskedEmail)
      : emailMessage;
  }

  setWhenNullOrUndefined(value: string | undefined | null, setAs = ''): string {
    return value?.trim() ?? setAs;
  }

  loginCustTransform(queryParameterResponse: string, keyword: string): string {
    const responseSplitted = queryParameterResponse.split('|');
    let encryptedValue = '';
    responseSplitted.forEach((queryParam: string) => {
      const fieldName = queryParam.split('=')[0];
      if (fieldName.includes(keyword)) {
        encryptedValue = queryParam.split('=')[1];
      }
    });
    return encryptedValue;
  }

  getErrorMessageFromAem(
    aemContent: LoginModalContent,
    errorCode: string | null,
    responseCode?: string
  ): string {
    const { errors } = aemContent;
    if (!errorCode) {
      errorCode = `${DEFAULT_MODAL_ERROR_CODE}`;
    }
    const errorDetails: ErrorMessage | undefined = errors.errorMessages.find(
      (element) => {
        if (this.wifiPortalService.getQueryParamsWFP().isWFP) {
          return element.errorId === `WFP_MSG_${responseCode}`;
        } else {
          return element.errorId === errorCode;
        }
      }
    );
    return errorDetails?.errorMessageDescription || '';
  }

  isRecognitionRoute(): boolean {
    if (RECOGNITION_PATHNAME_REGEX.test(window.location.pathname)) {
      const loginModalDiv = document.querySelector(
        '.login-modal'
      ) as HTMLElement;
      if (loginModalDiv) {
        loginModalDiv.classList.remove('d-none');
      }
      this.recognitionRoute.next(true);
    }
    return RECOGNITION_PATHNAME_REGEX.test(window.location.pathname);
  }

  getRefreshURLParams() {
    const currentPage = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);
    const refreshURL = urlParams.get(REFRESH_URL) ?? '';
    const fromAdmin = !!urlParams.get('fromAdmin');
    const decodeURL = decodeURI(refreshURL);
    return {
      currentPage,
      decodeURL,
      fromAdmin
    };
  }

  loginIsFalse(response: loginAndGetDashboardResponse) {
    return (
      response?.loggedIn === false &&
      response?.isLoggedIn === false &&
      response?.isWarmState === false
    );
  }

  isValidityCheckForFormSubmit(
    userInputFields: LoginAuthUserInputFields | undefined,
    authConfig: OauthAEMConfig
  ) {
    return authConfig?.PING_FLOW_ENABLED &&
      userInputFields?.userName &&
      userInputFields?.password
      ? true
      : false;
  }

  accountRecoveryAnalytics(viewName: string): void {
    if (viewName === VIEW_NAME.FORGOT_LOGIN_SCREEN) {
      this.analytics.dispatchCustomEvent(
        ANALYTICS_EVENTS_LOGIN.FORGOT_LOGIN_LOADED,
        true
      );
    } else if (viewName === VIEW_NAME.RESET_PASSWORD_SCREEN) {
      this.analytics.dispatchCustomEvent(
        ANALYTICS_EVENTS_LOGIN.RESET_PASSWORD_LOADED,
        true
      );
    } else {
      return;
    }
  }
}
